import { useCreateTag, useTags } from 'features/tags';
import React, { useCallback, useMemo } from 'react';
import { TagSearchMenu } from 'shared/components/ui/tag-search-menu';
import { defaultTagColorOption } from 'shared/constants';
import {
  useRelationships,
  useRelationshipsMap,
} from 'shared/hooks/use-relationships';
import { useUpdateRelationshipsReferences } from 'shared/hooks/use-update-relationships-references';
import { ID, IDPrefixes } from 'shared/types/id';
import { RelationshipUpdate } from 'shared/types/relationship';
import { Task } from 'shared/types/task';
import {
  createPrefixId,
  getPrefixedId,
  getPrefixedIdPrefix,
} from 'shared/utils/prefix-id';

export type TagsSubMenuProps = Omit<
  React.ComponentProps<typeof TagSearchMenu>,
  'tags' | 'value' | 'onChange' | 'onCreate' | 'onOutside'
> & {
  tasks: Task[];
  onClose: () => void;
};

export const TagsSubMenu: React.FC<TagsSubMenuProps> = ({
  tasks,
  onClose,
  ...rest
}) => {
  const tags = useTags();
  const relationships = useRelationships();
  const relationshipsMap = useRelationshipsMap(relationships);
  const updateRelationships = useUpdateRelationshipsReferences();
  const { submit: createTag } = useCreateTag();

  // Compute tags that all selected tasks have in common
  const commonTagIds = useMemo(() => {
    if (!tasks.length) {
      return [];
    }

    // Get tag IDs for each task using relationshipsMap
    const taskTagArrays = tasks.map((task) => {
      const taskPrefixedId = createPrefixId(task.id, IDPrefixes.Task);
      const relatedIds = relationshipsMap[taskPrefixedId] ?? [];
      return relatedIds
        .filter((id) => getPrefixedIdPrefix(id) === IDPrefixes.Tag)
        .map((id) => getPrefixedId(id));
    });

    // Find intersection of all tag arrays
    return taskTagArrays.reduce((common, taskTags) => {
      return common.filter((tagId) => taskTags.includes(tagId));
    }, taskTagArrays[0] ?? []);
  }, [tasks, relationshipsMap]);

  const handleTagChange = useCallback(
    (newTagIds: ID[]) => {
      // Determine which tags were added and which were removed
      const addedTagIds = newTagIds.filter((id) => !commonTagIds.includes(id));
      const removedTagIds = commonTagIds.filter(
        (id) => !newTagIds.includes(id),
      );

      // Build relationship updates
      const relationshipUpdates: RelationshipUpdate[] = [];

      // Handle added tags
      addedTagIds.forEach((tagId) => {
        const tagPrefixedId = createPrefixId(tagId, IDPrefixes.Tag);
        const taskPrefixedIds = tasks.map((task) =>
          createPrefixId(task.id, IDPrefixes.Task),
        );

        relationshipUpdates.push({
          id: tagPrefixedId,
          add: taskPrefixedIds,
        });
      });

      // Handle removed tags
      removedTagIds.forEach((tagId) => {
        const tagPrefixedId = createPrefixId(tagId, IDPrefixes.Tag);
        const taskPrefixedIds = tasks.map((task) =>
          createPrefixId(task.id, IDPrefixes.Task),
        );

        relationshipUpdates.push({
          id: tagPrefixedId,
          remove: taskPrefixedIds,
        });
      });

      if (relationshipUpdates.length > 0) {
        updateRelationships(relationshipUpdates);
      }

      onClose();
    },
    [commonTagIds, tasks, updateRelationships, onClose],
  );

  const handleTagCreate = useCallback(
    async (name: string) => {
      if (!name) {
        return;
      }

      const newTag = await createTag({
        name,
        color: defaultTagColorOption.value,
      });

      // Add the newly created tag to all selected tasks
      handleTagChange([...commonTagIds, newTag.id]);
    },
    [createTag, commonTagIds, handleTagChange],
  );

  return (
    <TagSearchMenu
      {...rest}
      tags={tags}
      value={commonTagIds}
      onChange={handleTagChange}
      onCreate={handleTagCreate}
      onOutside={onClose}
    />
  );
};
